import React, { useState, useEffect } from 'react';
import { posAPI, eventsAPI } from '../../../../services/api';
import { useAuthStore } from '../../../../store/authStore';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import { toast } from 'react-hot-toast';
import {
  Users,
  Ticket,
  Plus,
  Calendar,
  Activity,
  Shield,
  Mail,
} from 'lucide-react';
import './PosAgentManager.css';

const PosAgentManager = () => {
  const { user } = useAuthStore();
  const [agents, setAgents] = useState([]);
  const [allocationsByAgent, setAllocationsByAgent] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Modal State
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [allocationLimit, setAllocationLimit] = useState(50);
  const [assigning, setAssigning] = useState(false);

  const fetchAllocationsForAgent = React.useCallback(async (agentId, shouldToast = true) => {
    try {
      return await posAPI.getAgentAllocations(agentId);
    } catch (err) {
      console.error('Error fetching allocations for agent', agentId, err);
      if (shouldToast) {
        toast.error('Failed to load allocations for agent');
      }
      return [];
    }
  }, []);

  const fetchAgents = React.useCallback(async () => {
    try {
      const data = await posAPI.getSaleAgents();
      setAgents(data);

      // Preload allocations/stats for each agent
      const allocationsEntries = await Promise.all(
        data.map(async (agent) => {
          const allocs = await fetchAllocationsForAgent(agent.id, false);
          return [agent.id, allocs];
        })
      );
      setAllocationsByAgent(Object.fromEntries(allocationsEntries));
    } catch (err) {
      console.error('Error fetching agents:', err);
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, [fetchAllocationsForAgent]);

  const handleOpenAssignModal = async (agent) => {
    setSelectedAgent(agent);
    setSelectedEventId('');
    setAllocationLimit(10);
    setShowModal(true);
    try {
      // Fetch upcoming events scoped to role
      const isAdmin = user?.isAdmin || user?.admin;
      const isPlanner = user?.isPlanner || user?.planner;

      let response;
      if (isAdmin) {
        response = await eventsAPI.getAll({ size: 50, sort: 'startTime,desc' });
      } else if (isPlanner) {
        response = await eventsAPI.getByPlanner(user.id);
      } else {
        response = [];
      }

      const eventsList = Array.isArray(response)
        ? response
        : response?.content || response?.data?.content || response?.data || [];
      setEvents(eventsList);

      // Refresh allocations for this agent to ensure latest limits
      const allocs = await fetchAllocationsForAgent(agent.id);
      setAllocationsByAgent((prev) => ({ ...prev, [agent.id]: allocs }));
    } catch (err) {
      console.error('Error loading events:', err);
      toast.error('Failed to load events');
    }
  };

  useEffect(() => {
    if (selectedAgent?.id && selectedEventId) {
      const allocations = allocationsByAgent[selectedAgent.id] || [];
      const existing = allocations.find(a => a.eventId === selectedEventId);
      setAllocationLimit(existing ? existing.allocationLimit : 10);
    }
  }, [selectedAgent, selectedEventId, allocationsByAgent]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!selectedEventId) {
      toast.error('Please select an event');
      return;
    }

    setAssigning(true);
    try {
      await posAPI.assignAgent({
        agentId: selectedAgent.id,
        eventId: selectedEventId,
        allocationLimit: parseInt(allocationLimit, 10),
        assignedByUserId: user.id
      });

      toast.success(`Assigned ${selectedAgent.firstName} to event!`);

      // Refresh allocations for this agent so the panel shows the latest numbers
      const updatedAllocs = await fetchAllocationsForAgent(selectedAgent.id, false);
      setAllocationsByAgent((prev) => ({ ...prev, [selectedAgent.id]: updatedAllocs }));

      setShowModal(false);
      setSelectedEventId('');
      setAllocationLimit(50);
    } catch (err) {
      console.error('Assignment error:', err);
      toast.error('Failed to assign agent');
    } finally {
      setAssigning(false);
    }
  };

  const getInitials = (agent) => {
    const first = agent?.firstName?.[0] || '';
    const last = agent?.lastName?.[0] || '';
    const letters = `${first}${last}`.trim();
    return letters || (agent?.email?.[0] || '?').toUpperCase();
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="pos-agent-page">
      <div className="pos-manager-header">
        <div className="pos-header-content">
          <div className="pos-header-title">POS Agent Control</div>
          <div className="pos-header-subtitle">Assign field sellers to events</div>
        </div>
        <div className="pos-header-actions">
          <div className="pos-stat-pill">
            <Users size={16} />
            <span className="pos-stat-value">{agents.length} Agents</span>
          </div>
          <div className="pos-status-badge">
            <Activity size={14} /> Live Roster
          </div>
        </div>
      </div>

      {agents.length === 0 ? (
        <div className="pos-empty-state">
          <Shield size={48} className="pos-empty-icon" />
          <div className="pos-empty-text">No sale agents found. Users must have the SALE_AGENT role.</div>
        </div>
      ) : (
        <div className="pos-agent-grid">
          {agents.map(agent => (
            <div className="pos-agent-card" key={agent.id}>
              <div className="pos-card-header">
                <div className="pos-agent-identity">
                  <div className="pos-agent-avatar">{getInitials(agent)}</div>
                  <div className="pos-agent-info">
                    <h3>{agent.firstName} {agent.lastName}</h3>
                    <div className="pos-agent-email">
                      <Mail size={12} /> {agent.email}
                    </div>
                  </div>
                </div>
                <div className="pos-role-chip">Agent</div>
              </div>

              {allocationsByAgent[agent.id]?.length ? (
                <div className="pos-allocations-list">
                  {allocationsByAgent[agent.id].map(access => (
                    <div key={access.id} className="pos-allocation-item">
                      <div className="pos-event-name">
                        <Calendar size={12} style={{ marginRight: 6 }} />
                        {access.eventName || 'Event'}
                      </div>
                      <div className="pos-agent-name-subtitle">
                        Agent: {agent.firstName} {agent.lastName}
                      </div>
                      <div className="pos-allocation-stats">
                        <div className="pos-stat-box">
                          <div className="pos-stat-label">Limit</div>
                          <div className="pos-stat-num">
                            {access.allocationLimit > 2000000000 ? 'Unl' : access.allocationLimit}
                          </div>
                        </div>
                        <div className="pos-stat-box">
                          <div className="pos-stat-label">Sold</div>
                          <div className="pos-stat-num sold">{access.soldCount ?? 0}</div>
                        </div>
                        <div className="pos-stat-box">
                          <div className="pos-stat-label">Left</div>
                          <div className={`pos-stat-num remaining ${(access.remaining ?? 0) < 5 ? 'low' : ''}`}>
                            {access.remaining === 2147483647 ? 'Unl' : access.remaining ?? 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="pos-empty-allocations">
                  No active event assignments
                </div>
              )}

              <button
                className="pos-action-btn primary"
                onClick={() => handleOpenAssignModal(agent)}
              >
                <Plus size={16} /> Assign to Event
              </button>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="pos-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="pos-modal" onClick={e => e.stopPropagation()}>
            <div className="pos-modal-header">
              <div className="pos-modal-title">
                <Ticket size={24} className="text-blue-500" />
                Assign Event
              </div>
              <div className="pos-modal-subtitle">
                Assign <strong>{selectedAgent?.firstName}</strong> to sell tickets for an upcoming event.
              </div>
            </div>

            <form onSubmit={handleAssign}>
              <div className="pos-form-group">
                <label className="pos-label">Select Event</label>
                <div className="pos-input-wrapper">
                  <select
                    className="pos-select"
                    value={selectedEventId}
                    onChange={e => setSelectedEventId(e.target.value)}
                    required
                  >
                    <option value="">-- Choose an Event --</option>
                    {events.map(ev => (
                      <option key={ev.id} value={ev.id}>
                        {ev.title} ({new Date(ev.startTime).toLocaleDateString()})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="pos-form-group">
                <label className="pos-label">Ticket Allocation Limit</label>
                <div className="pos-input-wrapper">
                  <input
                    className="pos-input"
                    type="number"
                    min="1"
                    value={allocationLimit}
                    onChange={e => setAllocationLimit(e.target.value)}
                    required
                  />
                  <span className="pos-helper-text">Maximum tickets this agent can sell.</span>
                </div>
              </div>

              <div className="pos-modal-actions">
                <button
                  type="button"
                  className="pos-btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="pos-btn-primary"
                  disabled={assigning}
                >
                  {assigning ? 'Assigning...' : 'Confirm Assignment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PosAgentManager;
